import type { ReactNode } from 'react'
import { useTranslation } from '../localization/useTranslation'
import type { SearchProduct } from '../search/searchClient'
import { Glyph, IconCheck } from '../ui/icons'
import { ImageCarousel } from '../ui/ImageCarousel'
import { formatCategoryLabel } from '../util/category'
import { isDisplayableProductMetadata } from '../util/productMetadata'
import { useMoneyFmt } from '../util/useMoney'
import { DetailDialog } from './DetailDialog'

/**
 * "Is this mine?" match inspection for a visual-search result — a thin adapter that maps a
 * `SearchProduct` onto the shared `DetailDialog` (image gallery hero, colour, reward). The same
 * dialog backs the add-by-category picker; only the props differ.
 */
interface MatchDetailDialogProps {
  trigger: ReactNode
  product: SearchProduct
  defaultImage?: string
  /** Whether this is a high-confidence match (rank-relative — the score scale is unspecified). */
  strong?: boolean
  /**
   * Label for the dismiss button. Defaults to the localized "back to matches" (photo search); the
   * text search passes its "back to results" instead.
   */
  backLabel?: string
  onConfirm: () => void
}

export function MatchDetailDialog({
  trigger,
  product,
  defaultImage,
  strong,
  backLabel,
  onConfirm,
}: MatchDetailDialogProps) {
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  const primary = product.image || defaultImage
  // Prefer the full gallery; fall back to the single primary image until the endpoint sends it.
  const gallery = product.images && product.images.length > 0 ? product.images : primary ? [primary] : []
  const catName = product.category?.name
  const showCat = catName && catName.toLowerCase() !== 'unknown'
  const showColor = isDisplayableProductMetadata(product.color)

  return (
    <DetailDialog
      trigger={trigger}
      hero={
        gallery.length > 0 ? <ImageCarousel images={gallery} alt={product.name} /> : <Glyph name="shirt" />
      }
      badge={
        strong ? (
          <span className="match-badge">
            <IconCheck /> {t('match.strong')}
          </span>
        ) : undefined
      }
      eyebrow={showCat ? formatCategoryLabel(catName as string) : undefined}
      title={product.name}
      subtitle={showColor ? t('match.colour', { color: product.color }) : undefined}
      rewardLabel={t('match.estimatedReward')}
      rewardValue={fmt(product.tradeInValue)}
      backLabel={backLabel ?? t('match.backToMatches')}
      confirmLabel={t('match.confirm')}
      onConfirm={onConfirm}
    />
  )
}
